import React from 'react';
import { Document, Text, StyleSheet } from '@react-pdf/renderer';
import { SubjectStatus } from '../../../types';
import type { MarksPrint } from '../../../types/examination';
import {
  Page, DataTable,
  pdfColors, pdfSpacing, fmtClassName, fmtMarks,
  type DataTableColumn, type SchoolBranding,
} from '../../../utils/pdf';
import GradeSummary from './GradeSummary';
import { MARK_RED } from './scoreMarks';

type Student = MarksPrint['students'][number];

interface Props {
  report: MarksPrint;
  branding: SchoolBranding;
  generatedAt: string;
}

/** A paper that wasn't marked as sat, and how its row reads. */
const NOT_SAT: Partial<Record<SubjectStatus, { label: string; color: string }>> = {
  [SubjectStatus.PENDING]: { label: 'Pending', color: '#d97706' },
  [SubjectStatus.ABSENT]: { label: 'Absent', color: MARK_RED },
  [SubjectStatus.NOT_APPEARED]: { label: 'Not appeared', color: pdfColors.textMuted },
};
const GRADE_FALLBACK = '#1e3a8a';

const styles = StyleSheet.create({
  strong: { fontWeight: 600, color: pdfColors.textPrimary },
  empty: {
    marginTop: pdfSpacing.lg, paddingVertical: 36, textAlign: 'center', fontSize: 9, color: pdfColors.textMuted, fontStyle: 'italic',
    borderWidth: 0.6, borderColor: pdfColors.dividerStrong, borderStyle: 'dashed', borderRadius: 4,
  },
});

/**
 * One subject's marks for an exam, class or section: the school's
 * letterhead, the class average and how the grades fell, then every
 * student's marks, percentage and grade — in the school's grade colours.
 */
const MarksReportDocument: React.FC<Props> = ({ report, branding, generatedAt }) => {
  const cls = fmtClassName(report.className);
  const gradeColor = (name: string | null) => report.grades.find(g => g.name === name)?.color || GRADE_FALLBACK;
  const manySections = new Set(report.students.map(s => s.section).filter(Boolean)).size > 1;

  // How the grades fell — every grade in the scale, then who was absent.
  const sat = report.students.filter(s => s.status === SubjectStatus.PRESENT);
  const tiles = [
    ...report.grades.map(g => ({ label: g.name, value: sat.filter(s => s.grade === g.name).length, color: gradeColor(g.name) })),
    { label: 'Absent', value: report.students.filter(s => s.status === SubjectStatus.ABSENT).length, color: NOT_SAT[SubjectStatus.ABSENT]!.color },
  ];

  const mark = (s: Student, value: string, color?: string) => {
    const off = NOT_SAT[s.status];
    return <Text style={{ color: off?.color ?? color ?? pdfColors.textPrimary, fontWeight: 600 }}>{off ? off.label : value}</Text>;
  };
  const columns: Array<DataTableColumn<Student>> = [
    { key: 'n', header: '#', width: 26, align: 'center', render: (_s, i) => <Text style={{ color: pdfColors.textMuted }}>{i + 1}</Text> },
    { key: 'roll', header: 'Roll no.', width: 56, align: 'center', render: s => <Text style={styles.strong}>{s.rollNumber ?? '—'}</Text> },
    { key: 'name', header: 'Student', flex: 1, render: s => s.name },
    ...(manySections ? [{ key: 'section', header: 'Section', width: 54, render: (s: Student) => s.section ?? '—' }] : []),
    { key: 'obtained', header: 'Obtained', width: 82, align: 'center', render: s => mark(s, fmtMarks(s.obtainedMarks)) },
    { key: 'max', header: 'Max', width: 40, align: 'center', render: () => fmtMarks(report.maxMarks) },
    { key: 'pct', header: 'Percentage', width: 64, align: 'center', render: s => (NOT_SAT[s.status] ? '—' : `${(s.percentage || 0).toFixed(1)}%`) },
    {
      key: 'grade', header: 'Grade', width: 52, align: 'center',
      render: s => (s.status === SubjectStatus.NOT_APPEARED ? '—' : mark(s, s.grade || '—', gradeColor(s.grade))),
    },
  ];

  const scope = report.section ? `${cls} · Section ${report.section}` : cls;
  return (
    <Document title={`Marks Report — ${report.exam} — ${scope} — ${report.subject}`} author={branding.name}>
      <Page
        branding={branding}
        meta={{
          title: `Marks Report — ${report.exam}`,
          subtitle: `${scope}  ·  ${report.subject}`,
          generatedAt,
        }}
        official={{ details: [
          { value: scope },
          { label: 'Subject', value: report.subject },
          { label: 'Max marks', value: fmtMarks(report.maxMarks) },
        ] }}
      >
        {report.students.length === 0 ? (
          <Text style={styles.empty}>No student records found for this exam, class, section and subject.</Text>
        ) : (
          <>
            <GradeSummary average={report.classAverage} tiles={tiles} />
            <DataTable columns={columns} rows={report.students} zebra compact />
          </>
        )}
      </Page>
    </Document>
  );
};

export default MarksReportDocument;
